/**
 * todoRepeat — the repeat presets and their plain-words labels (the to-dos
 * upgrade, 2026-10-04).
 *
 * The design gives a SHORT PRESET LIST, never a rule builder: every weekday ·
 * every week on a day · every two weeks · monthly on a day · monthly on the
 * first/second/…/last weekday · last business day · quarterly · yearly · N days
 * after I finish it. The weekday- and date-shaped ones are drawn from the
 * to-do's own due date ("Every week on Fri" for a Friday to-do), so the list
 * always reads true for the to-do in front of the person.
 *
 * The rule is stored in the existing `recurrence_type` + `recurrence_config`
 * columns — no schema change — and the server's calculateNextDueDate
 * (todoService.ts) understands every shape below. `config.label` carries the
 * words the person picked so the chip never re-derives something different.
 */

export interface RepeatRule {
  type: string;
  config: Record<string, unknown>;
}

export interface RepeatPreset {
  key: string;
  label: string;
  rule: RepeatRule;
}

const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const NTH = ['', 'first', 'second', 'third', 'fourth'];

function anchorDate(dueIso: string | null | undefined, now: Date): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dueIso ?? ''));
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** Which occurrence of its weekday a date is: 1–4, or -1 for the last one. */
function nthOfMonth(d: Date): number {
  const nth = Math.ceil(d.getDate() / 7);
  const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return d.getDate() + 7 > daysInMonth ? -1 : nth;
}

export function repeatPresetsFor(dueIso: string | null | undefined, now: Date = new Date()): RepeatPreset[] {
  const d = anchorDate(dueIso, now);
  const wd = d.getDay();
  const dom = d.getDate();
  const nth = nthOfMonth(d);
  const nthWord = nth === -1 ? 'last' : NTH[nth];
  const presets: RepeatPreset[] = [
    { key: 'weekdays', label: 'Every weekday', rule: { type: 'weekdays', config: {} } },
    { key: 'weekly', label: `Every week on ${DOW_SHORT[wd]}`, rule: { type: 'weekly', config: { weekday: wd } } },
    { key: 'biweekly', label: 'Every 2 weeks', rule: { type: 'weekly', config: { interval: 2 } } },
    { key: 'monthly_day', label: `Monthly on day ${dom}`, rule: { type: 'monthly', config: { day_of_month: dom } } },
    { key: 'monthly_nth', label: `Monthly on the ${nthWord} ${DOW_LONG[wd]}`, rule: { type: 'monthly', config: { nth, weekday: wd } } },
    { key: 'monthly_lbd', label: 'Last business day of the month', rule: { type: 'monthly', config: { last_business_day: true } } },
    { key: 'quarterly', label: 'Quarterly', rule: { type: 'quarterly', config: {} } },
    { key: 'yearly', label: 'Yearly', rule: { type: 'yearly', config: {} } },
    { key: 'after7', label: '7 days after I finish it', rule: { type: 'after_completion', config: { days: 7 } } },
  ];
  return presets.map((p) => ({ ...p, rule: { ...p.rule, config: { ...p.rule.config, label: p.label } } }));
}

function parseConfig(config: unknown): Record<string, unknown> {
  if (!config) return {};
  if (typeof config === 'object') return config as Record<string, unknown>;
  try { return JSON.parse(String(config)) || {}; } catch { return {}; }
}

/** The repeat in plain words, or null when it does not repeat. */
export function repeatLabel(type: string | null | undefined, config?: unknown): string | null {
  const t = String(type ?? '');
  if (!t || t === 'one_time') return null;
  const c = parseConfig(config);
  if (typeof c.label === 'string' && c.label.trim()) return c.label.trim();
  switch (t) {
    case 'daily': return 'Every day';
    case 'weekdays': return 'Every weekday';
    case 'weekly': {
      const interval = Number(c.interval) || 1;
      if (c.weekday != null) return interval > 1 ? `Every ${interval} weeks on ${DOW_SHORT[Number(c.weekday) % 7]}` : `Every week on ${DOW_SHORT[Number(c.weekday) % 7]}`;
      return interval > 1 ? `Every ${interval} weeks` : 'Every week';
    }
    case 'monthly':
      if (c.last_business_day) return 'Last business day of the month';
      if (c.nth != null && c.weekday != null) {
        const n = Number(c.nth);
        return `Monthly on the ${n === -1 ? 'last' : NTH[n] || `${n}th`} ${DOW_LONG[Number(c.weekday) % 7]}`;
      }
      return c.day_of_month ? `Monthly on day ${c.day_of_month}` : 'Monthly';
    case 'quarterly': return 'Quarterly';
    case 'yearly': return 'Yearly';
    case 'after_completion': return `${Number(c.days) || 7} days after I finish it`;
    default: return 'Repeats';
  }
}

/** The payload a create/update sends for a picked preset (or "does not repeat"). */
export function repeatPayload(rule: RepeatRule | null): { recurrence_type: string; recurrence_config: string | null } {
  if (!rule) return { recurrence_type: 'one_time', recurrence_config: null };
  return { recurrence_type: rule.type, recurrence_config: JSON.stringify(rule.config) };
}
